import React, { useEffect, useState } from 'react'
import { OnboardingForm } from 'src/features/OnboardingForm'
import { FormikHelpers } from 'formik'
import { ApiOnboardingSurveyRequestBody } from 'BackendApi'
import queryString from 'query-string'
import { useUserSurvey } from '@app/api/postUserSurvey'
import { useHistory } from 'react-router-dom'
import { sendToast } from '@app/store/actions/ApplicationConfigurationActions'
import { parseApiError } from '@app/api/utils/error'
import { useAppDispatch } from '@app/store/hooks'
import { trackEvent } from '@app/dataTracking'
import { appRoutes } from '@app/routes'

const makeEmptyOnboardingForm = (): ApiOnboardingSurveyRequestBody => ({
  // Company Information (Step 1)
  position: '',
  companyName: '',
  industry: '',
  capacity: '',
  country: '',
  sourceApp: '',
  budget: '',
  destinationApp: '',
  // Goal Information (Step 2)
  goal: '',
  secondaryGoal: '',
})

export const OnboardingSurvey: React.FC = () => {
  const { mutateAsync: submitSurvey, isLoading: isSavingApi } = useUserSurvey()

  const history = useHistory()
  const dispatch = useAppDispatch()

  const initialValues = makeEmptyOnboardingForm()
  const [calendlyStep, setCalendlyStep] = useState<boolean>(false)
  const [calendlyUrl, setCalendlyUrl] = useState<string>('')
  const [finishClicked, setFinishClicked] = useState<boolean>(false)
  // parse query params (used in Step1 to show "Surprise!" banner)
  const loginParams = queryString.parse(window.location.search)
  const [redirectUri, setRedirectUri] = useState<string | undefined>(undefined)
  const confirmFinishClicked = () => {
    sessionStorage.clear()
    setFinishClicked(true)
  }

  const handleSubmit = async (
    formValues: ApiOnboardingSurveyRequestBody,
    formikHelpers: FormikHelpers<ApiOnboardingSurveyRequestBody>
  ) => {
    await submitSurvey(
      { requestBody: formValues },
      {
        onSuccess: async (response) => {
          formikHelpers.setSubmitting(false)
          // Track the event when the onboarding survey is completed
          trackEvent({
            eventName: 'OnboardingSurveyCompleted',
            feature: 'OnboardingSurvey',
            sendEventToIntercom: true,
          })

          const bbuLink = response.data?.result.bbuLink
          const redirectUrl = `${appRoutes.nbeeWelcome.makeUrl()}?${queryString.stringify(
            {
              ...loginParams,
              ...(bbuLink ? { bbuLink } : {}),
            }
          )}`

          setRedirectUri(redirectUrl)
          if (response.data?.result.calendlyUrl == null) {
            // Redirect to WelcomePage, passing bbuLink if present
            // WelcomePage will then redirect to bbuLink or /nbee/bridge/new
            history.push(redirectUrl)
            setFinishClicked(true)
            // Clear session storage to remove the flag
            sessionStorage.clear()
          } else {
            setCalendlyUrl(response.data?.result.calendlyUrl)
            setCalendlyStep(true)
            console.log('setCalendlyStep : ' + calendlyStep)
          }
        },
        onError: (error) => {
          dispatch(
            sendToast({
              title: 'Error',
              messages: [parseApiError(error).message],
              color: 'negative',
            })
          )
        },
      }
    )
  }

  // Track the event when the onboarding survey starts
  useEffect(() => {
    trackEvent({
      eventName: 'OnboardingSurveyStarted',
      feature: 'OnboardingSurvey',
      sendEventToIntercom: true,
    })
  }, [])

  // Display the onboarding survey only if the user has been redirected to it
  useEffect(() => {
    const hasSessionAccess =
      sessionStorage.getItem('onboarding_survey_access') === 'true'
    const parsedUrl = queryString.parse(window.location.search)
    const hasQueryAccess = parsedUrl.lbfsgn === 'true'

    // If there's no flag in session storage
    // or the query parameter is not set,
    // redirect the user to the step 1 of nbee
    if (!hasSessionAccess || !hasQueryAccess) {
      history.replace(appRoutes.nbeeStep1New.makeUrl())
    }
  }, [history])

  useEffect(() => {
    if (finishClicked) {
      if (redirectUri !== undefined) {
        history.replace(redirectUri)
      }
    }
  }, [history, finishClicked])

  return (
    <OnboardingForm
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isSavingApi={isSavingApi}
      calendlyStep={calendlyStep}
      calendlyUrl={calendlyUrl}
      onClickFinish={confirmFinishClicked}
    />
  )
}
